import { DateTime } from "luxon";

import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { MyleObjectType } from "@/Common/Models/Enum/MyleObjectType";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { IPortalShareableItem } from "@/Common/Models/Portal/UI/IPortalShareableItem";
import { ObstetricalFileContributionType } from "@/Patient/Models/ObstetricalFile/Enum/ObstetricalFileContributionType";
import { BaseObstetricalFileModel } from "./BaseObstetricalFileModel";

class ObstetricalFileModel extends BaseObstetricalFileModel implements IPortalShareableItem {
    public EncounterId: string;
    public PatientContactFacetId: string;
    public SharedItemId: string;
    public SharedItemDate: Date;
    public ContributionCreatedBy: string;
    public ContributionCreationDate: Date;
    public ContributionLastUpdatedBy: string;
    public ContributionLastUpdateDate: Date;
    public IsLatest: boolean;
    public IsLatestContribution: boolean;
    public ConsultationDateAtStartOfObstetricalFile: Date;
    public ContributionType: ObstetricalFileContributionType;

    // for PORTAL
    public SharedItem: SharedItem;
    public SharedItemMessages: SharedItemMessage[];
    public HasBeenSharedOnPreviousEncounters: boolean;

    constructor(fields?: Partial<ObstetricalFileModel>) {
        super();
        Object.assign(this, fields);
        this.setSharedItemMessagesSelectStatus();
    }

    public get IsNewObstetricalFile(): boolean {
        return this.ContributionType === ObstetricalFileContributionType.NewPregnancy;
    }

    // PORTAL FIELDS

    public getSharedItemMessages(): SharedItemMessage[] {
        this.SharedItemMessages && this.SharedItemMessages.forEach(p => {
            p.tmpIdForUI = SharedItemMessage.getTempIdForUi(p);
        });

        return this.SharedItemMessages;
    }

    public getSharedItem(): SharedItem {
        return this.SharedItem;
    }

    public getSharedItemId(): string {
        if (this && this.SharedItem)
            return this.SharedItem.SharedItemId;
        return null;
    }

    public getGlobalPortalStatus(): PortalSyncStatus {
        return PortalHelper.getGlobalPortalStatus(this.getSharedItem(), this.getSharedItemMessages());
    }

    public getItemTypeHeader(): string {
        return "";
    }

    public getItemTitle(): string {
        const title = `${cacheStore.GetString("FormLabel")}: ${cacheStore.GetString("ObstetricalFileLabel")}`
            .truncate(SharedItem.SubjectMaxLength);

        return `${title} (${cacheStore.GetString("AllPagesLabel")})`;
    }

    public getDefaultSubject(): string {
        return this.getItemTitle();
    }

    public getPortalObjectId(): string {
        return this.ContributionId.toString();
    }

    public getPortalLinkedItemType(): PortalLinkedItemType {
        return PortalLinkedItemType.ObstetricalFile;
    }

    public getSharedItemPortalStatus(): PortalSyncStatus {
        return PortalHelper.getLinkedItemPortalStatus(this.getSharedItem());
    }

    public hasBeenModifiedSinceLastShared(): boolean {
        // shared from current encounter
        if (this.SharedItemDate)
            return DateTime.fromISO(this.ContributionLastUpdateDate?.toString()) > DateTime.fromISO(this.SharedItem.LastStatusDate.toString());

        // shared on a previous encounter
        if (!this.SharedItemDate && this.HasBeenSharedOnPreviousEncounters)
            return this.ContributionLastUpdateDate > this.ContributionCreationDate;

        return false;
    }

    public hasBeenSharedOnPreviousEncounter(): boolean {
        return this.HasBeenSharedOnPreviousEncounters;
    }

    public getMyleObjectType(): MyleObjectType {
        return MyleObjectType.ObstetricalFile;
    }

    public updateShareableModel(sharedItem: SharedItem, sharedItemMessages: SharedItemMessage[]): void {
        this.SharedItem = sharedItem;
        this.SharedItemId = sharedItem.SharedItemId;
        this.SharedItemDate = sharedItem.DateCreated;
        this.SharedItemMessages = sharedItemMessages;
        this.setSharedItemMessagesSelectStatus();
    }

    public setSharedItemMessagesSelectStatus(): void {
        for (let message of this.SharedItemMessages || [])
            message.IsSelected = message.Status === PortalSyncStatus.Pending || message.Status === PortalSyncStatus.Viewed;
    }
}

export { ObstetricalFileModel };
